/** A dataspace's SIP trunk: how telephone calls reach its processes, and how they leave. */

export default {
	telephony: {
		title: "Telephony",
		hint: "Telephone calls for this dataspace's processes. A coms system — the PBX — sends calls to this server over SIP, or this dataspace registers on it as an extension. What a call does is up to the process.",
		retry: "Try again",
		saved: "Telephony settings saved.",
		removed: "Telephony settings removed.",
		remove: "Remove telephony settings",
		removeConfirm: "Remove this dataspace's telephony settings?",
		removeNote:
			"The settings and both stored passwords are deleted, and the server stops taking this dataspace's calls and withdraws its registration. This cannot be undone.",
		savedElsewhere: {
			title: "Saved elsewhere in the meantime",
			body: "Somebody saved these settings while you were editing. Your changes are kept; saving replaces what they saved with your version.",
			load: "Load the saved version",
		},
		list: {
			add: "Add {value}",
		},
		password: {
			set: "set",
			change: "Change",
			remove: "Clear",
			willRemove: "will be cleared when you save",
			new: "{label} (new)",
			keep: "Keep the current password",
		},
		status: {
			title: "Status",
			refresh: "Refresh",
			readAt: "Read at {time}",
			endpointOffTitle: "This server takes no SIP calls",
			endpointOff:
				"SIP is switched off on this server (SIP_ENABLED), so a trunk saved here does nothing until an administrator turns it on.",
			errorTitle:
				"The saved trunk cannot be used. Calls are refused until it is saved again.",
			endpoint: "SIP endpoint",
			on: "On",
			off: "Off",
			sipUri: "SIP address",
			sipUriHint: "Give this to the coms system as the trunk to send calls to.",
			copy: "Copy",
			copied: "Copied",
			activeCalls: "Calls in progress",
			routers: "Processes taking calls",
			routersHint: "Running processes of this dataspace that listen for calls.",
			noRouters:
				"No running process of this dataspace listens for calls, so every call goes to what is set under Unclaimed calls.",
			registration: {
				title: "Registration",
				registered: "Registered",
				registering: "Registering…",
				failed: "Failed",
				until: "until {time}",
				none: "Not registering",
				again: "Register again",
				againStarted: "Registering again with the stored credentials.",
			},
		},
		callsIn: {
			title: "Calls in",
			hint: "The coms system sends calls to this server's SIP address. A call is recognised by the digest login below, or by the address it comes from together with the number it dialled.",
			enabled: "Take and place calls",
			enabledHelp:
				"Off, the trunk keeps its settings but takes no calls and registers nowhere.",
			username: "Digest username",
			usernameHelp:
				"What the coms system logs in with. A username belongs to one dataspace on this server.",
			usernameTaken: "Another dataspace already uses this username.",
			password: "Digest password",
			passwordHelp: "Sealed when saved, and never shown again.",
			sources: "Allowed source addresses",
			sourcesPlaceholder: "Add an address or network…",
			sourcesHint: "An IP address, or a network such as 192.0.2.0/24",
			sourcesHelp:
				"Calls from these addresses are matched to this dataspace by the number they dial.",
			sourcesInvalid: "{value} is neither an IP address nor a network like 192.0.2.0/24.",
			numbers: "Numbers",
			numbersPlaceholder: "Add a number or extension…",
			numbersHint: "A number, an extension, or a prefix ending in *",
			numbersHelp:
				"The numbers this dataspace answers. A * at the end matches every number that starts so, as in +4930*. Empty: every number.",
			numbersInvalid: "{value} cannot be a number in this list.",
			numbersWildcard: "A * is a wildcard only at the end of a number.",
		},
		registration: {
			title: "Registration",
			hint: "Register this dataspace on the coms system as an extension and receive calls there, instead of — or as well as — being called.",
			registrar: "Registrar",
			registrarPlaceholder: "pbx.example.com:5060",
			registrarHelp: "The coms system's address, host or host:port. Empty: no registration.",
			extension: "Extension",
			extensionHelp: "The extension to register as.",
			authUsername: "Authentication username",
			authUsernameHelp: "Only when the coms system's authentication ID is not the extension.",
			password: "Registration password",
			expires: "Renewal interval (seconds)",
			expiresHelp: "How long a registration lasts before it is renewed.",
		},
		callsOut: {
			title: "Calls out",
			hint: "How calls placed from this dataspace, and transfers, leave.",
			proxy: "Outbound proxy",
			proxyHelp:
				"The host or host:port that calls are sent through. Empty: the registrar. With neither, a process cannot place calls.",
			callerId: "Caller ID",
			callerIdHelp:
				"The number or name shown on a call placed from here, unless the process names one. Empty: the extension.",
		},
		alternis: {
			title: "Alternis",
			hint: "How a call reaches Alternis, for transcription and suggestions.",
			integration: "Integration",
			comsSystem: "Via the coms system",
			comsSystemHelp:
				"The coms system already puts each call into Alternis, as for any call. Notitia forwards nothing.",
			notitia: "Via Notitia",
			notitiaHelp:
				"The coms system knows nothing of Alternis. The process creates the session, and Notitia forwards the call's audio.",
			rtpAddress: "Alternis RTP address",
			rtpAddressPlaceholder: "alternis.example.com:35000",
			rtpAddressHelp:
				"The host:port of Alternis's RTP socket, where Notitia forwards a call's audio. Empty: the Alternis host the process uses, port 35000.",
		},
		unclaimed: {
			title: "Unclaimed calls",
			hint: "A call has to be claimed by a process of this dataspace. This is what happens when none does in time, or when the one that did is gone.",
			timeout: "Wait for a process (seconds)",
			timeoutHelp: "How long a call waits to be claimed.",
			then: "Then",
			reject: "Reject the call",
			rejectHelp:
				"The coms system gets the status below. A call that was already answered is hung up.",
			redirect: "Redirect to a number",
			redirectHelp:
				"The call goes to another number instead. A call that was already answered is transferred there.",
			status: "Rejection status",
			statusHelp:
				"The SIP status the coms system receives. 480 is “temporarily unavailable”, 486 “busy here”.",
			target: "Number to redirect to",
			targetHelp:
				"An E.164 number such as +4930123456, or another number or extension the coms system understands.",
		},
		advanced: {
			title: "Advanced",
			maxCalls: "Calls at once",
			maxCallsHelp: "How many calls of this dataspace may be in progress at the same time.",
			advertised: "Advertised address",
			advertisedHelp:
				"The host or host:port this server gives the coms system for replies and audio, when the coms system reaches it under another address than the server's own.",
		},
		problems: {
			range: "Between {min} and {max}.",
			host: "Not a host. Write a name or an address with an optional :port — no scheme such as sip:, no path, no spaces.",
			port: "Needs a port, as host:port.",
			tooMany: "At most {max} entries.",
			text: "At most {max} characters, and no line breaks or other control characters.",
			inboundPassword: "A digest username needs a password.",
			registrationUsername: "A registrar needs the extension to register as.",
			fallbackTarget: "A redirect needs a number to send the call to.",
		},
	},
};
